<template>
  <div class="sales_return__report">
    <ReportFormat :records="tax_reports_records" @show_result="show_result" :is_table_show="false" :formSubmitBtnText="'Download'" :isLoading="loading">
      <template #report-form>
        <div class="vx-row pt-5 px-5">
          <div class="vx-col w-full lg:w-5/12 md:w-1/2">
            <div class="vx-row mb-2 items-center">
              <div class="vx-col w-2/5 text-right">
                <h6>From Date</h6>
              </div>
              <div class="vx-col w-3/5 relative">
                <date-picker :appendToBody="false" v-model="fromDate" placeholder="MM-DD-YYYY" format="MM-DD-YYYY" valueType="format" class="w-full" name="Report From Date" />
              </div>
            </div>

            <div class="vx-row mb-2 items-center">
              <div class="vx-col w-2/5 text-right">
                <h6>Tax Type</h6>
              </div>
              <div class="vx-col w-3/5">
                <select-2
                  class="w-full"
                  autocomplete
                  :value="taxType"
                  @input="(elem) => (taxType = elem && elem.value)"
                  placeholder="Please Select Tax Type"
                  :extra="[{ label: 'All', value: null }]"
                  :options="taxTypes"
                />
              </div>
            </div>
          </div>

          <div class="vx-col w-full lg:w-5/12 md:w-1/2">
            <div class="vx-row mb-2 items-center">
              <div class="vx-col w-2/5 text-right">
                <h6>To Date</h6>
              </div>
              <div class="vx-col w-3/5 relative">
                <date-picker :appendToBody="false" v-model="toDate" placeholder="MM-DD-YYYY" format="MM-DD-YYYY" valueType="format" class="w-full" name="Report To Date" />
              </div>
            </div>
          </div>
        </div>
      </template>
    </ReportFormat>
  </div>
</template>

<script>
import XLSX from 'xlsx'
import moment from 'moment'
import DatePicker from 'vue2-datepicker'
import 'vue2-datepicker/index.css'
import { dateMixins } from '@/mixins/dateMixins.js'
import { paymentMixins } from '@/utils/paymentTypes'
import { taxTypes } from '@/utils/taxTypes.js'
import { mapState, mapMutations } from 'vuex'
import ReportFormat from '@/views/reports/components/ReportFormat.vue'
import Select2 from '@/components/custom/form-elements/Select2.vue'

export default {
  name: 'SalesReturnReport',

  components: {
    ReportFormat,
    DatePicker,
    Select2
  },

  data() {
    return {
      page: 1,
      limit: 0,
      search: '',
      date: 'MM-dd-yyyy',
      fromDate: moment().format('MM-DD-YYYY'),
      taxType: null,
      toDate: moment().format('MM-DD-YYYY'),
      taxTypes,
      loading: false
    }
  },

  mixins: [paymentMixins, dateMixins],

  methods: {
    moment,

    ...mapMutations('report', { set_state: 'SET_STATE' }),

    async show_result() {
      const payload = {
        page: this.page,
        limit: this.limit,
        search: this.search,
        taxType: this.taxType || null,
        fromDate: moment(this.fromDate).format('YYYY-MM-DD'),
        toDate: moment(this.toDate).format('YYYY-MM-DD')
      }

      this.$store
        .dispatch('report/getTaxReport', payload)
        .then(() => {
          this.loading = false
        })
        .catch((err) => {
          const { message } = err
          this.loading = false
          this.$vs.notify({
            title: 'Error',
            text: message,
            iconPack: 'feather',
            icon: 'icon-alert-circle',
            color: 'danger'
          })
        })
    },

    async downloadTaxreport() {
      const wb = XLSX.utils.book_new()

      // csv headers
      const columnHeader = ['#no', 'Invoice Number', 'Sales Date', 'Customer Name', 'City Tax', 'State Tax', 'County Tax', 'Excise Tax', 'Tax Amount']

      const data = this.tax_reports_records.map((item, index) => ({
        '#no': index + 1,
        'Invoice Number': item.sales_code,
        'Sales Date': dateMixins.methods.formatDate(item.sales_date),
        'Customer Name': item.customer ? item.customer.business_name : '',
        'City Tax': item.tot_city_tax_amt - ((item.sales_return && paymentMixins.methods.mathRound(item.sales_return.tot_city_tax, 2)) || 0),
        'State Tax': item.tot_state_tax_amt - ((item.sales_return && paymentMixins.methods.mathRound(item.sales_return.tot_state_tax, 2)) || 0),
        'County Tax': item.tot_county_tax_amt - ((item.sales_return && paymentMixins.methods.mathRound(item.sales_return.tot_county_tax, 2)) || 0),
        'Excise Tax': item.tot_excise_tax_amt - ((item.sales_return && paymentMixins.methods.mathRound(item.sales_return.tot_excise_tax, 2)) || 0),
        'Tax Amount': item.tot_taxes_amt - ((item.sales_return && paymentMixins.methods.mathRound(item.sales_return.tot_taxes_amt, 2)) || 0)
      }))

      const ws = XLSX.utils.aoa_to_sheet([columnHeader], { origin: 'A2' })

      XLSX.utils.sheet_add_aoa(ws, [['tax report']], { origin: 'A1' })

      XLSX.utils.sheet_add_json(ws, data, { origin: 'A4', skipHeader: true })

      if (this.tax_reports_records.length === 0) {
        XLSX.utils.sheet_add_aoa(ws, [['No Record Found']], { origin: 'A4' })
      } else {
        XLSX.utils.sheet_add_aoa(
          ws,
          [
            [
              'total',
              null,
              null,
              null,
              paymentMixins.methods.mathRound(this.calculate_total_tax_by_name('tot_city_tax_amt', 'tot_city_tax'), 2),
              paymentMixins.methods.mathRound(this.calculate_total_tax_by_name('tot_state_tax_amt', 'tot_state_tax'), 2),
              paymentMixins.methods.mathRound(this.calculate_total_tax_by_name('tot_county_tax_amt', 'tot_county_tax'), 2),
              paymentMixins.methods.mathRound(this.calculate_total_tax_by_name('tot_excise_tax_amt', 'tot_excise_tax'), 2),
              paymentMixins.methods.mathRound(this.calculate_total_tax, 2)
            ]
          ],
          { origin: 'A3' }
        )
      }

      // eslint-disable-next-line init-declarations
      let mergedCells

      if (this.tax_reports_records.length) {
        mergedCells = [
          { s: { r: 0, c: 0 }, e: { r: 0, c: 8 } },
          { s: { r: 2, c: 0 }, e: { r: 2, c: 3 } }
        ]
      } else {
        mergedCells = [{ s: { r: 0, c: 0 }, e: { r: 0, c: 8 } }]
      }

      ws['!merges'] = mergedCells

      XLSX.utils.book_append_sheet(wb, ws, 'Sheet 1')
      XLSX.writeFile(wb, 'Tax Report.xlsx')
    }
  },

  computed: {
    ...mapState('report', ['reports_data', 'records_loading']),

    tax_reports_records() {
      return this.reports_data.reduce((acc, item) => {
        if (item.paid_amount > 0) {
          acc.push(item)
        }

        return acc
      }, [])
    },

    calculate_total_tax_by_name() {
      return (name, return_tax) => {
        return this.tax_reports_records.reduce((acc, item) => {
          return acc + (item[name] - ((item.sales_return && item.sales_return[return_tax]) || 0))
        }, 0)
      }
    },

    calculate_total_tax() {
      return this.tax_reports_records.reduce((acc, item) => {
        return acc + (item.tot_taxes_amt - ((item.sales_return && item.sales_return.tot_taxes_amt) || 0))
      }, 0)
    }
  },

  watch: {
    async reports_data() {
      await this.downloadTaxreport()
    }
  },
  filters: {
    // currencyFilter rounded to 2 point
    currencyFilter(value) {
      return paymentMixins.methods.mathRound(Number(value), 2)
    }
  },

  async beforeDestroy() {
    await this.set_state({ action: 'reports_data', data: [] })
  }
}
</script>

<style></style>
